import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { addCountryLabels } from "../utils/countryLabels";

const ESRI_LIGHT = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const ESRI_LIGHT_LABELS = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const ESRI_DARK_LABELS = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTR = "Tiles &copy; Esri";
const WORLD_GEOJSON = "https://cdn.jsdelivr.net/gh/holtzy/D3-graph-gallery@master/DATA/world.geojson";

let worldGeoPromise;
const loadWorldGeo = () => {
  if (!worldGeoPromise) {
    worldGeoPromise = fetch(WORLD_GEOJSON).then((response) => {
      if (!response.ok) throw new Error("world map");
      return response.json();
    });
  }
  return worldGeoPromise;
};

const GLOW = { normal: "#5ee9ff", warning: "#f5c451", emergency: "#ff5d8f", offline: "#7eb6d6" };
const STATUS_COLOR = { normal: "#22c55e", warning: "#f59e0b", emergency: "#ef4444", offline: "#94a3b8" };
const RANK = { emergency: 3, warning: 2, normal: 1, offline: 0 };

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

const pinStyle = (site, selectedSerial, isDark) => {
  const color = isDark ? GLOW[site.status] : STATUS_COLOR[site.status];
  const picked = site.serials.includes(selectedSerial);
  return {
    color,
    halo: { radius: picked ? 8 : 7, fillColor: color },
    marker: {
      radius: picked ? 5 : 4,
      color: isDark ? color : picked ? "#1e293b" : "#fff",
      weight: picked ? 2 : 1.5,
      fillColor: isDark ? "#f4fbff" : color,
    },
  };
};

const siteTooltip = (site) =>
  `<b>${escapeHtml(site.name)}</b><br/>${escapeHtml(site.country)}<br/>H-LINK ${site.hlinks.length} · Sensors ${site.sensors}`;

/** World map of H-LINK sites, styled like the Installation Info world view. */
export default function DashboardSiteMap({ hlinks, selectedSerial, onSelect, isDark }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const baseLayerRef = useRef(null);
  const labelLayerRef = useRef(null);
  const countryLabelsRef = useRef(null);
  const pinsRef = useRef(new Map());
  const linkRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const [mapReady, setMapReady] = useState(false);

  /** H-LINKs of the same site share one pin. */
  const sites = useMemo(() => {
    const bySite = new Map();
    hlinks.forEach((h) => {
      if (!h.pos) return;
      const key = `${h.company}|${h.country}|${h.site}`;
      if (!bySite.has(key)) bySite.set(key, { key, name: h.site, country: h.country, pos: h.pos, hlinks: [] });
      bySite.get(key).hlinks.push(h);
    });
    return [...bySite.values()].map((site) => ({
      ...site,
      serials: site.hlinks.map((h) => h.serial),
      sensors: site.hlinks.reduce((n, h) => n + h.sensors, 0),
      status: site.hlinks.map((h) => h.status).reduce((a, b) => (RANK[b] > RANK[a] ? b : a), "offline"),
    }));
  }, [hlinks]);

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return undefined;
    const map = L.map(mapEl.current, { zoomControl: true, attributionControl: false, worldCopyJump: true }).setView([20, 10], 2);
    const labelPane = map.createPane("siteLabels");
    labelPane.style.zIndex = 600;
    labelPane.style.pointerEvents = "none";
    map.createPane("siteLinks").style.zIndex = 620;
    map.createPane("sitePins").style.zIndex = 650;
    mapRef.current = map;
    setMapReady(true);
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(mapEl.current);
    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return undefined;
    if (baseLayerRef.current) baseLayerRef.current.remove();
    if (labelLayerRef.current) labelLayerRef.current.remove();
    if (countryLabelsRef.current) countryLabelsRef.current.remove();
    baseLayerRef.current = null;
    labelLayerRef.current = null;
    countryLabelsRef.current = null;
    const container = map.getContainer();
    let grid = container.querySelector(".ehd-map-grid");
    if (isDark) {
      container.style.background = "#0b1326";
      if (!grid) {
        grid = document.createElement("div");
        grid.className = "ehd-map-grid";
        container.appendChild(grid);
      }
      labelLayerRef.current = L.tileLayer(ESRI_DARK_LABELS, { attribution: ESRI_ATTR, maxZoom: 16, minZoom: 3, pane: "siteLabels" }).addTo(map);
      let cancelled = false;
      loadWorldGeo()
        .then((data) => {
          if (cancelled || !mapRef.current) return;
          if (baseLayerRef.current) baseLayerRef.current.remove();
          if (countryLabelsRef.current) countryLabelsRef.current.remove();
          baseLayerRef.current = L.geoJSON(data, {
            interactive: false,
            style: { fillColor: "#16324f", fillOpacity: 1, color: "#5b86b5", weight: 0.7 },
          }).addTo(map);
          baseLayerRef.current.bringToBack();
          countryLabelsRef.current = addCountryLabels(map, data, { pane: "siteLabels" });
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }
    container.style.background = "";
    if (grid) grid.remove();
    baseLayerRef.current = L.tileLayer(ESRI_LIGHT, { attribution: ESRI_ATTR, maxZoom: 16 }).addTo(map);
    labelLayerRef.current = L.tileLayer(ESRI_LIGHT_LABELS, { attribution: ESRI_ATTR, maxZoom: 16, pane: "overlayPane" }).addTo(map);
    return undefined;
  }, [isDark, mapReady]);

  /** Pins are rebuilt (and the view re-fitted) only when sites move; status changes restyle them in place. */
  const layoutKey = sites.map((s) => `${s.key}|${s.pos.lat}|${s.pos.lng}`).join(";");
  const sitesRef = useRef(sites);
  sitesRef.current = sites;
  const selectedRef = useRef(selectedSerial);
  selectedRef.current = selectedSerial;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    pinsRef.current.forEach(({ halo, marker }) => {
      halo?.remove();
      marker.remove();
    });
    pinsRef.current = new Map();
    linkRef.current?.remove();
    linkRef.current = null;
    const bounds = [];
    sitesRef.current.forEach((site) => {
      const at = [site.pos.lat, site.pos.lng];
      const style = pinStyle(site, selectedRef.current, isDark);
      const halo = isDark
        ? L.circleMarker(at, { ...style.halo, weight: 0, fillOpacity: 0.28, interactive: false, pane: "sitePins" }).addTo(map)
        : null;
      const marker = L.circleMarker(at, { ...style.marker, fillOpacity: 1, pane: "sitePins" }).addTo(map);
      if (isDark) {
        const el = marker.getElement();
        if (el) el.style.filter = `drop-shadow(0 0 3px ${style.color})`;
      }
      marker.bindTooltip(siteTooltip(site), { direction: "top" });
      marker.on("click", (event) => {
        L.DomEvent.stopPropagation(event);
        const current = sitesRef.current.find((s) => s.key === site.key);
        const target = current && (current.hlinks.find((h) => h.online) || current.hlinks[0]);
        if (target) onSelectRef.current?.(target);
      });
      pinsRef.current.set(site.key, { halo, marker });
      bounds.push(at);
    });
    if (isDark && bounds.length > 1) {
      const ordered = [...bounds].sort((a, b) => a[1] - b[1]);
      linkRef.current = L.polyline(ordered, {
        color: "rgba(140, 196, 230, 0.7)",
        weight: 1,
        dashArray: "4 5",
        interactive: false,
        pane: "siteLinks",
      }).addTo(map);
    }
    if (bounds.length === 1) map.setView(bounds[0], 4);
    else if (bounds.length > 1) map.fitBounds(bounds, { padding: [32, 32], maxZoom: 4 });
  }, [layoutKey, isDark, mapReady]);

  useEffect(() => {
    if (!mapReady) return;
    sites.forEach((site) => {
      const pin = pinsRef.current.get(site.key);
      if (!pin) return;
      const style = pinStyle(site, selectedSerial, isDark);
      if (pin.halo) {
        pin.halo.setRadius(style.halo.radius);
        pin.halo.setStyle({ fillColor: style.halo.fillColor });
      }
      const { radius, ...markerStyle } = style.marker;
      pin.marker.setRadius(radius);
      pin.marker.setStyle(markerStyle);
      if (isDark) {
        const el = pin.marker.getElement();
        if (el) el.style.filter = `drop-shadow(0 0 3px ${style.color})`;
      }
      pin.marker.setTooltipContent(siteTooltip(site));
    });
  }, [sites, selectedSerial, isDark, mapReady, layoutKey]);

  const unplaced = hlinks.filter((h) => !h.pos).length;

  return (
    <div className="site-map">
      <div className="site-map-legend">
        <span><i className="legend-dot normal" />Operational</span>
        <span><i className="legend-dot warning" />Warning</span>
        <span><i className="legend-dot emergency" />Alarm</span>
        <span><i className="legend-dot offline" />Offline</span>
      </div>
      <div ref={mapEl} className="site-map-canvas" />
      <div className="site-map-foot">
        {sites.length} site{sites.length === 1 ? "" : "s"} on map
        {unplaced ? ` · ${unplaced} H-LINK${unplaced === 1 ? "" : "s"} without location` : ""}
      </div>
    </div>
  );
}
